/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.0.0
 */

import { ICorporateTabMenuTranslation } from '@modules/internationalization/types/corporate';
import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging/loggingConstants';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { getCampaignsPageUrl } from '@pages/PageCampaign/utils/getCampaignsPageUrl';
import { RoutePath } from '@routes/constants/routesConstants';

import { ICorporateHeaderMenu } from './corporateHeaderTypes';

export const tabMenuItems = (translation: ICorporateTabMenuTranslation): ICorporateHeaderMenu => ({
    main: {
        accessKey: AccessComponentKeyEnum.TabMenuMain,
        dataAttr: 'main',
        isUrlSelected: (currentLocation: string) =>
            currentLocation === RoutePath.main || currentLocation === RoutePath.dashboard,
        order: 0,
        title: translation.main,
        url: RoutePath.main,
    },
    account: {
        accessKey: AccessComponentKeyEnum.TabMenuAccount,
        dataAttr: 'account',
        eventName: ELogBusinessEventName.USER_ACCOUNTS,
        eventType: ELogBusinessEventType.common,
        isUrlSelected: (currentLocation: string) => currentLocation === RoutePath.account,
        onboarding: EOnboardingComponentsId.linkAccounts,
        order: 0,
        title: translation.account,
        url: RoutePath.account,
    },
    servicesMobile: {
        accessKey: AccessComponentKeyEnum.TabMenuServicesSubscriber,
        dataAttr: 'subscribersMobile',
        eventName: ELogBusinessEventName.USER_MOBILE_SUBSCRIBERS,
        eventType: ELogBusinessEventType.common,
        onboarding: EOnboardingComponentsId.linkSubscribers,
        order: 1,
        isUrlSelected: (currentLocation: string) => currentLocation === RoutePath.subscriberMobile,
        title: translation.subscribersMobile,
        url: RoutePath.subscriberMobile,
    },
    history: {
        accessKey: AccessComponentKeyEnum.TabMenuHistory,
        dataAttr: 'history',
        onboarding: EOnboardingComponentsId.linkHistoryAndOrders,
        order: 3,
        isUrlSelected: (currentLocation: string) =>
            currentLocation === RoutePath.orders || currentLocation === RoutePath.inquiries,
        subItems: [
            {
                eventName: ELogBusinessEventName.USER_ORDER_HISTORY,
                eventType: ELogBusinessEventType.common,
                title: translation.orders,
                url: RoutePath.orders,
            },
            {
                eventName: ELogBusinessEventName.USER_INQUIRIES,
                eventType: ELogBusinessEventType.common,
                title: translation.inquiries,
                url: RoutePath.inquiries,
            },
        ],
        title: translation.history,
        url: '',
    },
    historyOrder: {
        accessKey: AccessComponentKeyEnum.TabMenuHistoryOrder,
        dataAttr: 'orders',
        eventName: ELogBusinessEventName.USER_ORDER_HISTORY,
        eventType: ELogBusinessEventType.common,
        onboarding: EOnboardingComponentsId.linkHistoryAndOrders,
        order: 3,
        isUrlSelected: (currentLocation: string) => currentLocation === RoutePath.orders,
        title: translation.orders,
        url: RoutePath.orders,
    },
    historyInquiry: {
        accessKey: AccessComponentKeyEnum.TabMenuHistoryInquiry,
        dataAttr: 'inquiries',
        eventName: ELogBusinessEventName.USER_INQUIRIES,
        eventType: ELogBusinessEventType.common,
        onboarding: EOnboardingComponentsId.linkHistoryAndOrders,
        order: 3,
        isUrlSelected: (currentLocation: string) => currentLocation === RoutePath.inquiries,
        title: translation.inquiries,
        url: RoutePath.inquiries,
    },
    offers: {
        accessKey: AccessComponentKeyEnum.TabMenuOffers,
        dataAttr: 'campaigns',
        order: 4,
        isUrlSelected: (currentLocation: string) => currentLocation === RoutePath.offers,
        title: translation.offers,
        url: getCampaignsPageUrl,
    },
    products: {
        accessKey: AccessComponentKeyEnum.TabMenuProducts,
        dataAttr: 'products',
        onboarding: EOnboardingComponentsId.linkProducts,
        order: 2,
        subItems: [
            {
                eventName: ELogBusinessEventName.USER_PAGE_PRODUCT_BROWSE,
                eventType: ELogBusinessEventType.common,
                title: translation.products,
                url: RoutePath.products,
            },
            {
                title: translation.cashback,
                url: RoutePath.cashback,
            },
        ],
        isUrlSelected: (currentLocation: string) =>
            currentLocation === RoutePath.products || currentLocation.includes(RoutePath.cashback),
        title: translation.products,
        url: '',
    },
    productsOnly: {
        accessKey: AccessComponentKeyEnum.TabMenuProductsOnly,
        dataAttr: 'products',
        eventName: ELogBusinessEventName.USER_PAGE_PRODUCT_BROWSE,
        eventType: ELogBusinessEventType.common,
        onboarding: EOnboardingComponentsId.linkProducts,
        order: 2,
        isUrlSelected: (currentLocation: string) => currentLocation === RoutePath.products,
        title: translation.products,
        url: RoutePath.products,
    },
    productsCashback: {
        accessKey: AccessComponentKeyEnum.TabMenuProductsCashback,
        dataAttr: 'cashback',
        order: 2,
        isUrlSelected: (currentLocation: string) => currentLocation.includes(RoutePath.cashback),
        title: translation.cashback,
        url: RoutePath.cashback,
    },
    collectiveProduct: {
        accessKey: AccessComponentKeyEnum.collectiveProduct,
        dataAttr: 'collectiveProduct',
        order: 1,
        isUrlSelected: (currentLocation: string) => currentLocation.includes(RoutePath.collectiveProduct),
        title: translation.collectiveProduct,
        url: RoutePath.collectiveProduct,
    },
    news: {
        accessKey: AccessComponentKeyEnum.TabMenuNews,
        dataAttr: 'news',
        order: 1,
        isUrlSelected: (currentLocation: string) => currentLocation.includes(RoutePath.news),
        title: translation.news,
        url: RoutePath.news,
    },
});
